import {StrictMode, useMemo} from 'react';
import {createRoot} from 'react-dom/client';
import {createBrowserRouter, RouterProvider} from 'react-router';

import {AuthenticatedLayout} from '#pages/layouts/authenticated/index.js';
import {sessionStore} from '#stores/session/index.js';
import type {Nullable} from '#types';
import type {Session} from '#types/session.js';
import {safeJsonParse} from '#utils/strings.js';

import {generateRoutes} from '../lib/router/routes.js';
import {App} from '../pages/_app.js';
import {chromeless, routes} from './router.js';
import {VortexDirectionProvider} from './vortex_direction_provider.js';

type RootProps = {
  props: {
    [k: `/${string}`]: Record<string, unknown>;
    session: Nullable<Session>;
  };
};

function Root({props: {session: _session, ...pageProps}}: RootProps) {
  const router = useMemo(() => {
    const children = generateRoutes(routes, pageProps);

    return createBrowserRouter([
      {
        path: '/',
        element: <App />,
        children: [
          ...generateRoutes(chromeless, pageProps),
          {
            element: <AuthenticatedLayout />,
            children,
          },
        ],
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <RouterProvider router={router} />;
}

function getInitialProps(domNode: HTMLElement | null): RootProps['props'] {
  if (!domNode) {
    return {session: null};
  }

  const dataProps = domNode.getAttribute('data-props');
  const parsedProps = safeJsonParse(dataProps, {});

  return {
    session: null,
    ...parsedProps,
  };
}

function init() {
  const mountPoint = document.getElementById('react');

  if (!mountPoint) {
    console.error('React mount point not found');
    return;
  }

  const initialProps = getInitialProps(mountPoint);
  const root = createRoot(mountPoint);

  /*
   * Safe to hydrate synchronously: server embeds `session` in data-props,
   * getInitialProps parses it synchronously, and Zustand setState is sync.
   * Doing this before render prevents first-paint races in session consumers.
   */
  sessionStore.getState().setSession(initialProps.session);

  root.render(
    <StrictMode>
      <VortexDirectionProvider element={mountPoint}>
        <Root props={initialProps} />
      </VortexDirectionProvider>
    </StrictMode>
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export {Root, getInitialProps};
